<template>
<div class="page-container-content tabgroup">
  <w-tool-tabs v-model="activeTabName">
    <div slot="tool">
      <w-hamburger-button ref="hamburgerButton">
        <template slot="popupContent">
          <w-perf-sliding-popup-view :selected-node="selectedNode" @change-page="changePage" />
        </template>
      </w-hamburger-button>
    </div>
    <el-tab-pane :label="tabs.summary.label" :name="tabs.summary.name" class="h100 w100">
      <w-performance-node-summary :active="tabs.summary.active" />
    </el-tab-pane>
    <el-tab-pane :label="tabs.analysis.label" :name="tabs.analysis.name">
      <w-performance-node-analysis :active="tabs.analysis.active" />
    </el-tab-pane>
    <el-tab-pane v-if="showOperationTab" :label="tabs.operation.label" :name="tabs.operation.name">
      <w-performance-node-operation :active="tabs.operation.active" />
    </el-tab-pane>
    <el-tab-pane :label="tabs.config.label" :name="tabs.config.name">
      <w-config-node :active="tabs.config.active" />
    </el-tab-pane>
    <el-tab-pane :label="tabs.event.label" :name="tabs.event.name">
      <w-performance-node-event :active="tabs.event.active" />
    </el-tab-pane>
    <el-tab-pane v-if="showDbmsFlowMapTab" :label="tabs['database-flow'].label" :name="tabs['database-flow'].name">
      <w-database-flow-map :active="tabs['database-flow'].active" />
    </el-tab-pane>
    <el-tab-pane v-if="showFlowAnalysisTab" :label="tabs['flow-analysis'].label" :name="tabs['flow-analysis'].name">
      <w-performance-node-flow-analysis :active="tabs['flow-analysis'].active" :obj-filter="flowObjFilter" />
    </el-tab-pane>
    <el-tab-pane v-if="showListviewTab" :label="tabs.listview.label" :name="tabs.listview.name">
      <w-performance-node-listview :active="tabs.listview.active" />
    </el-tab-pane>
    <el-tab-pane v-if="showVirtualmapTab" :label="tabs.virtualmap.label" :name="tabs.virtualmap.name">
      <w-virtual-map-frame :active="tabs.virtualmap.active"></w-virtual-map-frame>
    </el-tab-pane>
    <el-tab-pane v-if="showTransactionTab" :label="tabs.transaction.label" :name="tabs.transaction.name">
      <w-transaction-matrix :active="tabs.transaction.active" :default-params="transactionParams"/>
    </el-tab-pane>
    <el-tab-pane v-if="showWirelessmapTab" :label="tabs.wirelessmap.label" :name="tabs.wirelessmap.name">
      <w-performance-node-wireless-map :active="tabs.wirelessmap.active"></w-performance-node-wireless-map>
    </el-tab-pane>
  </w-tool-tabs>
  <w-resource-smart-analyzer-popup />
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { objType } from '@/constants/obj';
import RouterTab from '@/mixins/router/tab';
import WConfigNode from '@/views/config/node';
import WHamburgerButton from '@/components/button/HamburgerButton';
import { performanceNodeTabs } from '@/constants/performance';
import FlowAPI from '@/api/performance/flow-analysis/flow-analysis';
import WPerformanceNodeListview from '@/views/performance/node/listview';
import WVirtualMapFrame from '@/views/performance/components/virtual/VirtualMapFrame';
import WResourceSmartAnalyzerPopup from '@/components/popup/ResourceSmartAnalyzerPopup';
import WTransactionMatrix from '@/views/apm/transaction-matrix';
import WPerformanceNodeSummary from './summary';
import WPerformanceNodeAnalysis from './analysis';
import WPerformanceNodeEvent from './event';
import WDatabaseFlowMap from './database-flow-map';
import WPerformanceNodeFlowAnalysis from './flow-analysis';
import WPerfSlidingPopupView from './popup/PerfSlidingPopupView';
import WPerformanceNodeOperation from './operation';
import WPerformanceNodeWirelessMap from './wireless-map';

export default {
  name: 'WPerformanceNode',
  components: {
    WPerformanceNodeSummary,
    WPerformanceNodeAnalysis,
    WPerformanceNodeEvent,
    WDatabaseFlowMap,
    WHamburgerButton,
    WConfigNode,
    WPerfSlidingPopupView,
    WPerformanceNodeFlowAnalysis,
    WPerformanceNodeListview,
    WVirtualMapFrame,
    WTransactionMatrix,
    WResourceSmartAnalyzerPopup,
    WPerformanceNodeOperation,
    WPerformanceNodeWirelessMap,
  },
  mixins: [RouterTab],
  data() {
    return {
      objType,
      flowObjFilter: {},
      tabs: performanceNodeTabs,
    };
  },
  computed: {
    objInfoForCheckValidTab() {
      const node = this.selectedNode;
      return {
        objManagetypeId: node.obj.objManagetypeId,
        objtypeId: node.obj.objtypeId,
        objDefineId: node.obj.objDefineId,
        netdeviceFlowApplyFlag: node.netdeviceFlowApplyFlag,
        netinterfaceFlowApplyFlag: node.netinterfaceFlowApplyFlag,
        hypervisorType: node.hypervisorType,
      };
    },
    /**
     * [Flow 분석]탭의 권한
     * @return {Boolean} [description]
     * @author jda
     */
    authFlowAnalysis() {
      const roles = this.roles;
      return roles.some((role) => role === 'FLOW_ADMIN' || role === 'ADMIN');
    },
    /**
     * DBMS일 경우 [플로우맵] 탭을 표현한다.
     * @return {[type]} [description]
     * @author jda
     */
    showDbmsFlowMapTab() {
      return this.tabs['database-flow'].validate(this.objInfoForCheckValidTab, this.roles);
    },
    /**
     * 네트워크 장비 또는 인터페이스일 경우 [Flow 분석]탭을 표현한다.
     * @return {Boolean} [description]
     * @author jda
     */
    showFlowAnalysisTab() {
      return this.tabs['flow-analysis'].validate(this.objInfoForCheckValidTab, this.roles);
    },
    /**
     * 가상화 리스트 뷰 탭 제공 여부
     * @return {Boolean}
     */
    showListviewTab() {
      return this.tabs.listview.validate(this.objInfoForCheckValidTab, this.roles);
    },
    /**
     * 가상화 맵 탭 제공 여부
     * @return {Boolean}
     */
    showVirtualmapTab() {
      return this.tabs.virtualmap.validate(this.objInfoForCheckValidTab, this.roles);
    },
    /**
     * APM 트랜잭션 분포도 탭 표현 여부
     * @return {[type]} [description]
     * @author jda
     */
    showTransactionTab() {
      return this.tabs.transaction.validate(this.objInfoForCheckValidTab, this.roles);
    },
    /**
     * 운영 탭 표현 여부
     * @return {[type]} [description]
     * @author Song
     */
    showOperationTab() {
      return this.tabs.operation.validate(this.objInfoForCheckValidTab, this.roles);
    },
    /**
     * 무선 장비 맵 탭 표현 여부
     * @return {[type]} [description]
     * @author jeee12
     */
    showWirelessmapTab() {
      return this.tabs.wirelessmap.validate(this.objInfoForCheckValidTab, this.roles);
    },
    /**
     * 트랜잭션 분포도 로드하기위한 기본 파라메터 정의
     * @return {[type]} [description]
     * @author jda
     */
    transactionParams() {
      const selectedObjectId = this.selectedNode.obj.objId;
      // 논리그룹 트리에서 분포도를 접근하는 경우, objId로 데이터를 조회하기 때문에 이에 따른 타입을 추가한다.
      const selectedObjectType = 'obj';
      return { requestType: 'instance', selectedObjectId, selectedObjectType, ...this.$store.state.apm.transactionMatrixData };
    },
    ...mapGetters(['selectedNode', 'roles', 'activeManagetypeIds']),
  },
  watch: {
    /**
     * Flow분석 탭 권한을 가지고 있을시, 분석할 관리대상 리스트를 조회한다.
     * @type {Object}
     * @author jda
     */
    selectedNode: {
      immediate: true,
      handler() {
        if (this.authFlowAnalysis) {
          this.setFlowObjFilter();
        }
      },
    },
  },
  methods: {
    /**
     * 페이지가 변경된 경우 햄버거 버튼의 팝업을 닫는다.
     * @author Ju
     * @return {[type]} [description]
     */
    changePage() {
      this.$refs.hamburgerButton.closePopup();
    },
    /**
     * Flow분석할 관리대상을 조회한다.
     * Flow 분석 가능한 관리대상일 경우에만 Flow분석 탭이 표현된다.
     * @author jda
     */
    setFlowObjFilter() {
      const obj = this.selectedNode.obj;
      const treeType = this.$store.state.router.treeType;
      FlowAPI.getBasicFilter({ objId: obj.objId, objDefineId: obj.objDefineId, treeType })
        .then((response) => {
          if (response.data.success) {
            this.flowObjFilter = response.data.data;
          } else {
            this.flowObjFilter = {};
          }
        });
    },
    /**
     * 사용할 수 없는 탭인지 확인한다.
     * @author Ju
     */
    isDisabledTab(tab) {
      return (
        (tab === 'flow-analysis' && !this.showFlowAnalysisTab)
       || (tab === 'database-flow' && !this.showDbmsFlowMapTab)
       || (tab === 'listview' && !this.showListviewTab) // VMS
       || (tab === 'virtualmap' && !this.showVirtualmapTab) // VMS
       || (tab === 'transaction' && !this.showTransactionTab) // APM
       || (tab === 'operation' && !this.showOperationTab)
       || (tab === this.tabs.wirelessmap.name && !this.showWirelessmapTab) // WNMS
      );
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/performance/node/index.vue